/** Layer & sync DTOs. The annotation object schema itself lives in src/shared/annotations.ts. */
import type { AudienceGroup } from '../audience';

export type LayerShareTarget =
  | { type: 'all' }
  | { type: 'section'; section: Exclude<AudienceGroup, 'all'> }
  | { type: 'user'; userId: string };

export interface LayerDTO {
  id: string;
  sheetFileId: string;
  ownerId: string;
  ownerName: string;
  ownerIsEditor: boolean;
  ownerInactive: boolean;
  name: string;
  sheetRev: number;
  updatedAt: number;
  maxSeq: number;
  shares: LayerShareTarget[]; // only filled for the owner's own layers
  mine: boolean;
}

/**
 * POST /api/layers — idempotent create, or rename of an own layer. `sheetRev` (≤ the file's current
 * rev) is the sheet revision the marks were checked against; on an existing layer it only ever moves
 * forward, so re-sending the upsert with the current rev acknowledges a new sheet revision (clears
 * the "drawn on an older version" banner for everyone).
 */
export interface UpsertLayerRequest {
  id: string;
  sheetFileId: string;
  name: string;
  sheetRev: number;
  /** Re-confirming a layer on a new sheet revision: keep the server's current name (an existing layer). */
  keepName?: boolean;
}

export interface ShareLayerRequest {
  shares: LayerShareTarget[];
}

/** Wire format of one annotation row. `data` is the JSON-encoded annotation payload. */
export interface AnnotationRow {
  id: string;
  layerId: string;
  page: number;
  kind: 'highlight' | 'ink' | 'text' | 'stamp' | 'connector';
  data: string;
  hlcMs: number;
  deviceId: string;
  /**
   * Tombstone flag. On push any number marks a delete; the server stores (and pulls return) its own
   * receive time instead, so tombstone retention (180 days) never depends on a device clock.
   */
  deletedAt: number | null;
  seq?: number;
}

/** POST /api/sync/annotations — at most 50 ops / 64 KB per request. */
export interface PushAnnotationsRequest {
  ops: AnnotationRow[];
}
export interface PushAnnotationsResponse {
  results: { id: string; status: 'applied' | 'stale' | 'forbidden' }[];
}

/**
 * Continuation point of a layer that is being fetched in full across several pages (see
 * PullResponse.resume). Opaque to the client: send back exactly what the server returned.
 */
export interface PullResume {
  /** The client holds this layer's live rows up to this seq. */
  after: number;
  /** Server counter when the first page of this full fetch was read. */
  snapshot: number;
}

/** POST /api/sync/pull */
export interface PullRequest {
  sheetFileId: string;
  knownLayers: Record<string, number>; // layerId → cursor (from PullResponse.cursors)
  /**
   * Layers in the middle of a paged full fetch (from the previous response's `resume`, ≤ 200
   * entries). Ignored for layers that are also in knownLayers.
   */
  resume?: Record<string, PullResume>;
}
export interface PullResponse {
  layers: LayerDTO[];
  removed: string[];
  reset: string[];
  rows: AnnotationRow[];
  /**
   * true when `rows` was capped (SYNC_LIMITS.pullRows rows / pullDataChars of data, always whole
   * seq groups): apply this response, store `cursors`, then pull again.
   */
  more?: boolean;
  /**
   * Always sent by the server. The cursor to store for each layer after applying `rows` (and after
   * dropping the local content of `reset` layers). Use these, never max(row.seq): after a tombstone
   * purge the newest seq of a layer may be gone, and only these values are guaranteed not to trigger
   * another reset. A visible layer missing here (only when `more`) is listed in `resume` instead
   * (or, from an older server, has no content yet): leave it out of knownLayers on the next pull.
   */
  cursors?: Record<string, number>;
  /**
   * Always sent by the server; empty unless `more`. Layers whose FULL fetch (unknown, reset or
   * resumed layer) was cut by the page limit below the layer's purge watermark. The rows received
   * so far are valid: keep them, and send this map back as PullRequest.resume on the next pull
   * (keep it apart from cursors; never put these layers in knownLayers). In that next response a
   * resumed layer is NOT a full layer — do not clear its local objects — unless it is listed in
   * `reset` (the continuation is no longer valid; the layer comes in full from the start).
   * Unknown, non-resumed layers are full as before. If the paging loop is abandoned, drop `resume`
   * together with the partial content, or keep both and send `resume` later (the server re-checks it).
   */
  resume?: Record<string, PullResume>;
}

/**
 * GET /api/admin/orphan-layers[?sheet=<fileId>] (admin) — shared layers whose owner has left
 * (status inactive/erased), metadata only, for "Flytja lag" (POST /api/layers/:id/transfer).
 */
export interface OrphanLayerDTO {
  id: string;
  sheetFileId: string;
  songId: string;
  songTitle: string;
  name: string;
  ownerId: string;
  ownerName: string;
  shares: LayerShareTarget[];
  updatedAt: number;
}

/** PATCH /api/layers/:id (owner only). */
export interface RenameLayerRequest {
  name: string;
}

/** POST /api/layers/:id/transfer (admin; a layer shared with anyone, owner inactive/erased). */
export interface TransferLayerRequest {
  toUserId: string;
}

/** Reasons in the 422 `details` of PUT /api/layers/:id/share: [{ path: ['shares', i], message: reason }]. */
export type ShareTargetIssue = 'section_not_in_audience' | 'user_unavailable' | 'user_cannot_see_sheet';

/** Server-enforced limits of the layer / sync API. */
export const SYNC_LIMITS = {
  /** Ops per POST /sync/annotations. */
  pushOps: 50,
  /** Request body bytes per POST /sync/annotations (413 above). */
  pushBytes: 64 * 1024,
  /** Highest page index accepted by the envelope (the sheet's page_count applies too). */
  maxPage: 499,
  /** Characters of an op's `data`. */
  dataChars: 32_768,
  /** deviceId length. */
  deviceIdChars: 64,
  /** hlcMs further ahead of server time than this is clamped to server time + clockSkewMs. */
  clockSkewMs: 5 * 60_000,
  /** Entries in PullRequest.knownLayers (and, separately, in PullRequest.resume). */
  knownLayers: 200,
  /** Rows per pull response (more: true beyond). */
  pullRows: 5000,
  /** Characters of `data` per pull response (more: true beyond). */
  pullDataChars: 2_000_000,
  /** Targets per PUT /layers/:id/share. */
  shareTargets: 60,
  /** Layer name length. */
  layerName: 60,
} as const;
